import { Employee } from "@/models/employees";
import api from "@/services/api";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EmployeePagePhoneIcon from "@/assets/images/EmployeePagePhoneIcon.svg";
import EmployeeGeneralInfo from "../employeeGeneralInfo/employeeGeneralInfo";
import EmployeeDigitalWallet from "../employeeDigitalWallet/employeeDigitalWallet";
import EmployeeOrders from "../employeeOrders/employeeOrders";
import EmployeeAttachedDocs from "../employeeAttachedDocs/employeeAttachedDocs";
import ImportEmployees from "../import/import";
import { formatAmountWithoutDecimals } from "@/util";
import { useTranslation } from "react-i18next";
import popupService from "@/services/popupService";

const EmployeePage = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [employeeData, setEmployeeData] = useState<Employee>();
    async function getEmployeeData(employeeId: number | null): Promise<Employee | null> {
        try {
            const id = employeeId;
            const responsePromise = await api.getEmployeeData(id);
            return responsePromise.data;
        } catch (err) {
            await popupService.showApiError(err);
            navigate("/employees/");
            return null;
        }
    }

    function getComponent(pathname: string) {
        switch (pathname) {
            case "/employees/" + id + "/info":
                return <EmployeeGeneralInfo />;
            case "/employees/" + id + "/wallet":
                return <EmployeeDigitalWallet />;
            case "/employees/" + id + "/employeeOrders":
                return <EmployeeOrders />;
            case "/employees/" + id + "/docs":
                return <EmployeeAttachedDocs />;
            case "/employees/import":
                return <ImportEmployees />;
        }
    }

    async function startFetching() {
        const apiResponse = await getEmployeeData(Number(id));
        if (!apiResponse) return;
        setEmployeeData(apiResponse);
        if (employeeBreadcrumbStore.employeeName === "") {
            employeeBreadcrumbStore.update(apiResponse.firstName + " " + apiResponse.lastName);
        }
    }

    useEffect(() => {
        if (location.pathname === "/employees/" + id) {
            startFetching();
            navigate("/employees/" + id + "/info");
        }
    }, [location]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <div className="employee-page-container">
            <div className="employee-page-header">
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <div className="single-ellipsis" style={{ maxWidth: "527px" }}>
                        {employeeData?.firstName} {employeeData?.lastName}, {employeeData?.employeeNumber}
                    </div>
                    <div className="adaptive_padding" style={{ padding: "0 20px" }}>
                        |
                    </div>
                    <div>ID: {employeeData?.id}</div>
                    <div className="adaptive_padding" style={{ padding: "0 20px" }}>
                        |
                    </div>
                    <div>
                        {t("organization.UsedBalance")}{" "}
                        {formatAmountWithoutDecimals(employeeData?.usedBalance, employeeData?.defaultCurrencyId)}
                    </div>
                    <div className="adaptive_padding" style={{ padding: "0 20px" }}>
                        |
                    </div>
                    <div>
                        {t("employee.Wallet.CurrentBalance")}{" "}
                        {formatAmountWithoutDecimals(employeeData?.leftBalance!, employeeData?.defaultCurrencyId)}
                    </div>
                </div>
                <div className="employee-page-header-right">
                    <img src={EmployeePagePhoneIcon} alt="PhoneIcon" />
                    {employeeData?.mobilePhone}
                </div>
            </div>
            <div className="employee-page-content">{getComponent(location.pathname)}</div>
        </div>
    );
};

export default EmployeePage;
